import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query"
import { persistQueryClient } from "@tanstack/query-persist-client-core"
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister"
import * as Sentry from "@sentry/react-router"
import { setFullStoryUser } from "~/lib/fullStory.client"
import { setSentryUser } from "~/lib/sentry.client"

// Type for user data from the "user" query
interface UserQueryData {
  me: {
    id: string
    firstName?: string
    lastName?: string
    email: string
  }
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (increased for persistence)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
  // Global error handler - reports all errors to Sentry
  queryCache: new QueryCache({
    onError: (error) => {
      // Don't report 401 errors - they're expected and handled by auto-logout
      if ((error as { status?: number }).status !== 401) {
        Sentry.captureException(error)
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      // Don't report 401 errors - they're expected and handled by auto-logout
      if ((error as { status?: number }).status !== 401) {
        Sentry.captureException(error)
      }
    },
  }),
})

// Initialize persistence immediately since SSR is disabled
if (typeof window !== "undefined") {
  const localStoragePersister = createAsyncStoragePersister({
    storage: window.localStorage,
    key: "backflip-query-cache",
  })

  // Clear any existing session data from cache on app start
  queryClient.removeQueries({
    queryKey: ["plaid", "multiItemSession"],
  })
  queryClient.removeQueries({
    queryKey: ["plaid", "session"],
  })

  void persistQueryClient({
    queryClient,
    persister: localStoragePersister,
    maxAge: 1000 * 60 * 60 * 24, // 24 hours
    dehydrateOptions: {
      shouldDehydrateQuery: (query) => {
        // Don't persist session-related queries as they're ephemeral
        const queryKey = query.queryKey
        if (Array.isArray(queryKey) && queryKey.length >= 2) {
          // Don't persist plaid session queries
          if (
            queryKey[0] === "plaid" &&
            (queryKey[1] === "multiItemSession" || queryKey[1] === "session")
          ) {
            return false
          }
        }
        return true
      },
    },
  })

  // Track the last user ID we set to avoid duplicate calls
  let lastIdentifiedUserId: string | null = null

  // Subscribe to user query changes to set/clear user identity in FullStory and Sentry
  // This fires on: fresh fetch, cache restore from localStorage, invalidation/refetch, and logout
  queryClient.getQueryCache().subscribe((event) => {
    const queryKey = event.query.queryKey as string[]
    if (queryKey[0] !== "user") return

    if (event.type === "updated") {
      const data = event.query.state.data as UserQueryData | undefined
      const user = data?.me

      if (user && user.id !== lastIdentifiedUserId) {
        // User is set - identify them
        const displayName = [user.firstName, user.lastName]
          .filter(Boolean)
          .join(" ")

        // eslint-disable-next-line no-console
        console.log("[Identity] Setting user identity:", {
          id: user.id,
          displayName,
          email: user.email,
          source: "queryCache.subscribe",
        })

        setFullStoryUser(user.id, displayName, user.email)
        setSentryUser(user.id, user.email, displayName || undefined)
        lastIdentifiedUserId = user.id
      } else if (!user && lastIdentifiedUserId !== null) {
        // User is unset (logout) - clear identity
        // eslint-disable-next-line no-console
        console.log("[Identity] Clearing user identity (logout)", {
          source: "queryCache.subscribe",
        })

        setFullStoryUser(null, "", "")
        setSentryUser(null)
        lastIdentifiedUserId = null
      }
    }

    // Also handle query removal (e.g., queryClient.clear() on logout)
    if (event.type === "removed" && lastIdentifiedUserId !== null) {
      // eslint-disable-next-line no-console
      console.log("[Identity] Clearing user identity (query removed)", {
        source: "queryCache.subscribe",
      })

      setFullStoryUser(null, "", "")
      setSentryUser(null)
      lastIdentifiedUserId = null
    }
  })
}
